import React, { useState } from 'react';
import { Observer } from 'mobx-react-lite';
import GeoConfigsTabStore from './GeoConfigsTabStore';
import ServiceName from '../../../../entities/ServiceName';
import { useStyles } from './GeoConfigsTab.style';
import FilterInput from '../../../../shared_components/Inputs/FilterInput/FilterInput';

export const DISPLAY_SERVICES: ServiceConfig[] = [
    {
        key: ServiceName.MONEY_DELIVERY,
        label: 'Envío'
    },
    {
        key: ServiceName.PACKAGE_DELIVERY,
        label: 'Paquete'
    },
    {
        key: ServiceName.ARTICLE_DELIVERY,
        label: 'Artículo'
    }
];

export interface ServiceConfig {
    key: ServiceName;
    label: string;
    description?: string;
}

const GeoConfigsTab: React.FC = () => {
    const classes = useStyles();
    const [store] = useState(() => new GeoConfigsTabStore());

    const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

    const handleSearchChange = (text: string) => {
        store.setSearchTerm(text);

        if (debounceTimeout) clearTimeout(debounceTimeout);

        const timeout = setTimeout(() => {
            store.loadGeoConfigs();
        }, 800);

        setDebounceTimeout(timeout);
    };

    const handleServiceToggle = (
        municipalityId: string, 
        serviceName: ServiceName
    ) => {
        const isCurrentlyEnabled = store.isServiceEnabled(municipalityId, serviceName);
        store.updateMunicipalityService(municipalityId, serviceName, !isCurrentlyEnabled);
    };

    const renderToggleSwitch = (
        municipalityId: string,
        serviceName: ServiceName,
        isEnabled: boolean
    ) => (
        <label className={`${classes.toggle_switch} ${isEnabled ? classes.toggle_checked : ''}`}>
            <input
                type="checkbox"
                className={classes.toggle_input}
                checked={isEnabled}
                onChange={() => handleServiceToggle(municipalityId, serviceName)}
            />
            <span className={classes.toggle_slider}></span>
        </label>
    );

    const getServiceLabel = (serviceName: ServiceName): string => {
        const service = DISPLAY_SERVICES.find(s => s.key === serviceName);
        return service ? service.label : serviceName;
    };

    return (
        <Observer>
            {() => (
                <div className={classes.container}>
                    <div className={classes.search_container}>
                        <FilterInput value={store.searchTerm}
                            placeholder={'Buscar por nombre'}
                            onChange={handleSearchChange}/>
                    </div>
                    <div className={classes.table_container}>
                        {/* Table Header */}
                        <div className={classes.table_header}>
                            <div>Municipio</div>
                            <div style={{ textAlign: 'center' }}></div>
                            <div style={{ textAlign: 'center' }}>Servicios</div>
                            <div style={{ textAlign: 'center' }}></div>
                        </div>

                        {/* Table Content */}
                        {store.isLoading ? (
                            <div className={classes.loading_state}>
                                Carregando municípios... {store.isLoading}
                            </div>
                        ) : store.filteredGeoConfigs.length === 0 ? (
                            <div className={classes.empty_state}>
                                {store.searchTerm 
                                    ? `Nenhum município encontrado para "${store.searchTerm}"`
                                    : 'Nenhum município disponível'
                                }
                            </div>
                        ) : (
                            store.filteredGeoConfigs.map((municipality) => (
                                <div key={municipality.id} className={classes.table_row}>
                                    <div className={classes.town_name}>
                                        {municipality.name}
                                    </div>
                                    
                                    {/* Money Transfer */}
                                    <div className={classes.service_cell}>
                                        <div className={classes.service_label}>
                                            {getServiceLabel(ServiceName.MONEY_DELIVERY)}
                                        </div>
                                        {renderToggleSwitch(
                                            municipality.id,
                                            ServiceName.MONEY_DELIVERY,
                                            store.isServiceEnabled(municipality.id, ServiceName.MONEY_DELIVERY)
                                        )}
                                    </div>

                                    {/* Package Delivery */}
                                    <div className={classes.service_cell}>
                                        <div className={classes.service_label}>
                                            {getServiceLabel(ServiceName.PACKAGE_DELIVERY)}
                                        </div>
                                        {renderToggleSwitch(
                                            municipality.id,
                                            ServiceName.PACKAGE_DELIVERY,
                                            store.isServiceEnabled(municipality.id, ServiceName.PACKAGE_DELIVERY)
                                        )}
                                    </div>

                                    {/* Article Delivery */}
                                    <div className={classes.service_cell}>
                                        <div className={classes.service_label}>
                                            {getServiceLabel(ServiceName.ARTICLE_DELIVERY)}
                                        </div>
                                        {renderToggleSwitch(
                                            municipality.id,
                                            ServiceName.ARTICLE_DELIVERY,
                                            store.isServiceEnabled(municipality.id, ServiceName.ARTICLE_DELIVERY)
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </Observer>
    );
};

export default GeoConfigsTab;